import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

type AuthorStats = {
  totalIdeas: number;
  totalFollowers: number;
  totalVotes: number;
  activePolls: number;
};

type TopIdea = {
  id: string;
  title: string;
  identity_username: string;
  votes_count: number;
  followers_count: number;
};

type RecentMilestone = {
  id: string;
  idea_id: string;
  idea_title: string;
  title: string;
  status: 'planned' | 'in_progress' | 'completed';
};

type PollOption = {
  id: string;
  text: string | null;
  image_url: string | null;
  votes_count: number;
};

type PollResult = {
  id: string;
  idea_id: string;
  idea_title: string;
  title: string;
  total_votes: number;
  closed: boolean;
  ends_at: string;
  options: PollOption[];
};

export function useAuthorStats() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<AuthorStats>({
    totalIdeas: 0,
    totalFollowers: 0,
    totalVotes: 0,
    activePolls: 0
  });
  const [topIdeas, setTopIdeas] = useState<TopIdea[]>([]);
  const [recentMilestones, setRecentMilestones] = useState<RecentMilestone[]>([]);
  const [pollResults, setPollResults] = useState<PollResult[]>([]);

  useEffect(() => {
    if (user) {
      fetchAuthorStats();
    }
  }, [user]);

  const fetchAuthorStats = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Get all pen names for the author
      const { data: penNames } = await supabase
        .from('pen_names')
        .select('id')
        .eq('owner_id', user.id);

      // Build array of identity IDs (main profile + pen names)
      const identityIds = [user.id, ...(penNames?.map(p => p.id) || [])];

      // Fetch total ideas and followers
      const { data: ideasData } = await supabase
        .from('book_ideas')
        .select(`
          id,
          title,
          identity_username,
          votes:votes(count),
          followers:idea_follows(count)
        `)
        .in('identity_id', identityIds)
        .order('created_at', { ascending: false });

      // Fetch all polls
      const { data: pollsData } = await supabase
        .from('polls')
        .select(`
          id,
          idea_id,
          title,
          closed,
          ends_at,
          book_ideas!inner(title),
          options:poll_options(
            id,
            text,
            image_url,
            votes:poll_votes(count)
          )
        `)
        .in('idea_id', ideasData?.map(i => i.id) || [])
        .order('created_at', { ascending: false });

      // Fetch recent milestones
      const { data: milestonesData } = await supabase
        .from('milestones')
        .select(`
          id,
          idea_id,
          title,
          status,
          book_ideas!inner(title)
        `)
        .in('idea_id', ideasData?.map(i => i.id) || [])
        .order('created_at', { ascending: false })
        .limit(5);

      // Process the data
      if (ideasData) {
        // Set top ideas
        const processedIdeas = ideasData.map(idea => ({
          id: idea.id,
          title: idea.title,
          identity_username: idea.identity_username,
          votes_count: idea.votes[0]?.count || 0,
          followers_count: idea.followers[0]?.count || 0
        }));

        setTopIdeas(
          processedIdeas
            .sort((a, b) => b.votes_count - a.votes_count)
            .slice(0, 5)
        );

        // Count active polls
        const activePollsCount = (pollsData || []).filter(poll => 
          !poll.closed && new Date(poll.ends_at) > new Date()
        ).length;

        // Set overall stats
        setStats({
          totalIdeas: ideasData.length,
          totalFollowers: ideasData.reduce((sum, idea) => sum + (idea.followers[0]?.count || 0), 0),
          totalVotes: ideasData.reduce((sum, idea) => sum + (idea.votes[0]?.count || 0), 0),
          activePolls: activePollsCount
        });
      }

      // Set milestones
      if (milestonesData) {
        setRecentMilestones(
          milestonesData.map(m => ({
            id: m.id,
            idea_id: m.idea_id,
            idea_title: m.book_ideas.title,
            title: m.title,
            status: m.status
          }))
        );
      }

      // Set poll results
      if (pollsData) {
        setPollResults(
          pollsData.map(p => ({
            id: p.id,
            idea_id: p.idea_id,
            idea_title: p.book_ideas.title,
            title: p.title,
            closed: p.closed,
            ends_at: p.ends_at,
            total_votes: p.options.reduce((sum, opt) => sum + (opt.votes[0]?.count || 0), 0),
            options: p.options.map(opt => ({
              id: opt.id,
              text: opt.text,
              image_url: opt.image_url,
              votes_count: opt.votes[0]?.count || 0
            }))
          }))
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch author stats');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    stats,
    topIdeas,
    recentMilestones,
    pollResults
  };
}